'use client';

import {
  center,
  flex,
  text,
  vstack,
} from '@onbeam/business-styled-system/patterns';
import {
  ApplicationStatus,
  type Application as ApplicationItemType,
} from '../ApplicationsList.types';
import { applicationStyleMap } from '../ApplicationsList.data';
import { format } from 'date-fns';
import { Badge, Link } from '@onbeam/business-ui';
import { formatUsd } from '@onbeam/utils';
import { css } from '@onbeam/business-styled-system/css';
import { Drawer } from '@/components/shared/Drawer';
import { DrawerField } from '@/components/shared/DrawerField';

export const ApplicationItem = (data: ApplicationItemType) => {
  const {
    name: title,
    fundingGranted,
    applicant,
    primaryCategory,
    categories,
    createdAt,
    description,
    teamDescription,
    detailedBudget,
    startDate,
    endDate,
    milestones,
    rationale,
    progressReportingFrequency,
    demoUrl,
    ratedAt,
    status,
  } = data;

  if (!primaryCategory && !categories[0]) return null;

  const primaryCategoryStyles =
    applicationStyleMap[primaryCategory || categories[0]];

  const Icon = primaryCategoryStyles.icon;

  const Heading = (
    <div className={flex({ gap: '3', align: 'center' })}>
      <div
        className={center({
          p: '3',
          rounded: 'md',
          border: '1px solid',
          borderColor: 'opacity.sm',
          bg: 'mono.primaryFg',
        })}
      >
        <Icon />
      </div>
      <div className={vstack({ gap: '0.5' })}>
        <span className={text({ style: 'body-md' })}>{title}</span>
        <div
          className={flex({
            gap: '2',
            rowGap: '1',
            align: 'center',
            flexWrap: 'wrap',
          })}
        >
          <span
            className={text({
              style: 'body-md',
              color: 'mono.tertiary',
            })}
          >
            By {applicant}
          </span>
          {status === ApplicationStatus.APPROVED && (
            <Badge color="mono">{formatUsd(fundingGranted)}</Badge>
          )}
          {categories.map((category) => {
            const categoryStyles = applicationStyleMap[category];
            return (
              <Badge key={category} color={categoryStyles.color}>
                {categoryStyles.name}
              </Badge>
            );
          })}
        </div>
      </div>
    </div>
  );

  const CreationDate = (
    <span
      className={text({
        style: 'small-sm',
        fontWeight: 'bold',
        color: 'mono.tertiary',
      })}
    >
      {format(new Date(createdAt), 'MMM d, yyyy')}
    </span>
  );

  return (
    <li>
      <Drawer
        trigger={
          <button
            type="button"
            className={vstack({
              gap: '3',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'colors',
              md: {
                px: '5',
                py: '4',
                rounded: 'lg',
                gap: '6',
                _supportsHover: {
                  _hover: {
                    bg: 'mono.senary',
                  },
                },
                _focusVisible: {
                  bg: 'mono.senary',
                },
              },
            })}
          >
            <div
              className={flex({
                w: 'full',
                justify: 'space-between',
                align: 'center',
              })}
            >
              {Heading}
              <div className={css({ mdDown: { display: 'none' } })}>
                {CreationDate}
              </div>
            </div>
            <p className={text({ style: 'body-md', mt: '2' })}>{description}</p>
            <div className={css({ md: { display: 'none' } })}>
              {CreationDate}
            </div>
          </button>
        }
        title={title}
        description={description}
        hideTitle
        hideDescription
      >
        <div className={vstack({ gap: '10' })}>
          {Heading}
          <DrawerField title="Description" description={description} />
          <DrawerField title="Team" description={teamDescription} />
          {status === ApplicationStatus.APPROVED && fundingGranted && (
            <DrawerField
              title="Total funding awarded"
              description={formatUsd(fundingGranted)}
            />
          )}
          <DrawerField title="Detailed Budget" description={detailedBudget} />
          <DrawerField
            title="Expected timeline"
            description={
              <p>
                Start: {format(new Date(startDate), 'MMMM yyyy')}
                <br />
                Expected completion: {format(endDate, 'MMMM yyyy')}
              </p>
            }
          />
          <DrawerField title="Milestones" description={milestones} />
          <DrawerField title="Rationale" description={rationale} />
          <DrawerField
            title="Integration with Beam"
            description={categories
              .map((category) => applicationStyleMap[category].name)
              .join(', ')}
          />
          <DrawerField
            title="Progress Reporting"
            description={progressReportingFrequency}
          />
          <DrawerField
            title="MVP Link"
            description={
              <Link
                href={demoUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="blue"
              >
                {demoUrl}
              </Link>
            }
          />
          {status === ApplicationStatus.APPROVED && ratedAt && (
            <p className={text({ style: 'body-sm', color: 'mono.tertiary' })}>
              Approved by Beam Foundation & grant committee (
              {format(ratedAt, 'MMMM yyyy')})
            </p>
          )}
        </div>
      </Drawer>
    </li>
  );
};
